import React, { useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { useOwnerForm, useSiteConfig } from '../context/CMSContext';
import { useTrackEvent } from '../context/AnalyticsContext';
import { CheckCircle } from 'lucide-react';

const OwnerListing: React.FC = () => {
  const ownerForm = useOwnerForm();
  const siteConfig = useSiteConfig();
  const trackEvent = useTrackEvent();
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const apiBase = import.meta.env.VITE_CMS_API_URL || 'https://marveland-cms.jimsbond007.workers.dev';

  const handleChange = (id: string, value: string) => {
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      type: 'owner_listing',
      ...formData,
    };

    try {
      const res = await fetch(`${apiBase}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setSubmitted(true);
        setFormData({});
        trackEvent({ action: 'owner_listing_submit' });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      <Header />
      <main>
        <section className="relative bg-slate-900 py-24 md:py-32">
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900 to-slate-800" />
          <div className="container mx-auto px-4 relative z-10 pt-16">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{ownerForm.title}</h1>
            <p className="text-slate-300 text-lg max-w-2xl">{ownerForm.description}</p>
          </div>
        </section>

        <section className="py-20 bg-[#f5f3ee]">
          <div className="container mx-auto px-4 max-w-3xl">
            {submitted ? (
              <div className="rounded-2xl bg-white p-12 text-center shadow-lg">
                <CheckCircle className="mx-auto mb-6 h-16 w-16 text-green-600" />
                <h2 className="mb-4 text-2xl font-bold text-slate-900">提交成功</h2>
                <p className="text-slate-600">感謝您的資料，金晉物業代理的專業團隊會盡快與您聯絡。</p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="rounded-2xl bg-white p-8 md:p-12 shadow-lg space-y-6"
              >
                {ownerForm.fields.map((field) => (
                  <div key={field.id}>
                    <label htmlFor={field.id} className="mb-2 block font-semibold text-slate-700">
                      {field.label}
                      {field.required && <span className="ml-1 text-red-500">*</span>}
                    </label>
                    {field.type === 'select' ? (
                      <select
                        id={field.id}
                        required={field.required}
                        value={formData[field.id] || ''}
                        onChange={(e) => handleChange(field.id, e.target.value)}
                        className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-800 focus:border-gold-600 focus:outline-none focus:ring-2 focus:ring-gold-600/20"
                      >
                        <option value="">請選擇</option>
                        {field.options?.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : field.type === 'textarea' ? (
                      <textarea
                        id={field.id}
                        required={field.required}
                        rows={4}
                        value={formData[field.id] || ''}
                        onChange={(e) => handleChange(field.id, e.target.value)}
                        placeholder={field.placeholder}
                        className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-800 focus:border-gold-600 focus:outline-none focus:ring-2 focus:ring-gold-600/20"
                      />
                    ) : (
                      <input
                        id={field.id}
                        type={field.type}
                        required={field.required}
                        value={formData[field.id] || ''}
                        onChange={(e) => handleChange(field.id, e.target.value)}
                        placeholder={field.placeholder}
                        className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-800 focus:border-gold-600 focus:outline-none focus:ring-2 focus:ring-gold-600/20"
                      />
                    )}
                  </div>
                ))}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-lg bg-gold-600 px-8 py-4 font-bold text-white transition-colors hover:bg-gold-700 disabled:opacity-60"
                >
                  {loading ? '提交中...' : '提交放盤資料'}
                </button>

                <p className="text-center text-xs text-slate-500">
                  提交即表示您同意我們的服務條款。如有查詢，請致電 {siteConfig.name} (852) 3619 9530。
                </p>
              </form>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default OwnerListing;
